import React, { useRef, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Check, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { HOME } from "@/constants/testIds";

// Map admin program duration string → server-side catalog package_id
const durationToPackage = (duration) => {
  const m = String(duration || "").match(/\d+/);
  if (!m) return null;
  const n = m[0];
  if (n === "15") return "program_15";
  if (n === "30") return "program_30";
  if (n === "45") return "program_45";
  return null;
};

function TiltCard({ program, i, highlighted, onEnrol, checkingOut }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotX = useTransform(my, [0, 1], [6, -6]);
  const rotY = useTransform(mx, [0, 1], [-6, 6]);

  const handleMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };
  const days = program.duration.match(/\d+/)?.[0] || "";

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1200 }}
      className={`relative rounded-3xl p-6 sm:p-7 will-change-transform ${
        highlighted
          ? "bg-[#0B132B] text-white shadow-glow ring-1 ring-white/10 lg:scale-[1.04]"
          : "bg-white ring-1 ring-slate-200 shadow-soft"
      }`}
    >
      {program.badge && (
        <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-white text-[#0B132B] text-[10px] tracking-widest font-semibold shadow-elev">
          {program.badge}
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-[11px] tracking-[0.28em] uppercase ${highlighted ? "text-slate-400" : "text-slate-500"}`}>{program.duration}</div>
          <h3 className={`mt-2 font-display text-2xl leading-tight ${highlighted ? "text-white" : "text-[#0B132B]"}`}>{program.name}</h3>
        </div>
        <div className={`h-14 w-14 rounded-xl grid place-items-center relative ${highlighted ? "bg-white/10" : "bg-slate-100"}`}>
          <Calendar size={14} className={highlighted ? "text-slate-300" : "text-slate-500"} />
          <span className={`absolute inset-0 grid place-items-center pt-3 font-display font-semibold text-lg ${highlighted ? "text-white" : "text-[#0B132B]"}`}>{days}</span>
        </div>
      </div>
      <p className={`mt-3 text-sm leading-relaxed ${highlighted ? "text-slate-300" : "text-slate-600"}`}>{program.tagline || program.description}</p>
      <div className="mt-6 flex items-baseline gap-1">
        <span className={`font-display text-4xl font-semibold ${highlighted ? "text-white" : "text-[#0B132B]"}`}>{program.price}</span>
        <span className={`text-xs ${highlighted ? "text-slate-400" : "text-slate-500"}`}>onwards</span>
      </div>
      <ul className="mt-6 space-y-2.5">
        {(program.features || []).slice(0, 8).map((f) => (
          <li key={f} className={`flex items-start gap-2 text-[13px] ${highlighted ? "text-slate-300" : "text-slate-600"}`}>
            <Check size={14} className={highlighted ? "text-white mt-0.5" : "text-[#0B132B] mt-0.5"} />
            {f}
          </li>
        ))}
      </ul>
      <a href="#contact" data-testid={HOME.programCta(i)}
         onClick={(e) => { e.preventDefault(); onEnrol(program); }}
         className={`mt-7 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full text-sm font-medium transition-colors ${
           highlighted ? "bg-white text-[#0B132B] hover:bg-slate-100" : "bg-[#0B132B] text-white hover:bg-[#1C2541]"
         } ${checkingOut ? "opacity-70 pointer-events-none" : ""}`}>
        {checkingOut ? (<><Loader2 size={14} className="animate-spin" /> Preparing…</>) : (program.cta_label || "Enrol Now")}
      </a>
    </motion.div>
  );
}

export default function Programs({ programs }) {
  const list = (programs || []).filter((p) => p.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const [checkingOut, setCheckingOut] = useState(null);
  const [modal, setModal] = useState(null); // program being enrolled

  const startCheckout = async (program, buyer) => {
    const pkg = durationToPackage(program.duration);
    if (!pkg) {
      toast.error("This program isn't set up for online payment yet — please use the enquiry form.");
      return;
    }
    setCheckingOut(program.id || program.name);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg,
        origin_url: window.location.origin,
        full_name: buyer.full_name,
        email: buyer.email,
        phone: buyer.phone,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckingOut(null);
    }
  };

  return (
    <section id="programs" className="py-24 sm:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.28em] uppercase text-slate-500">Programs</div>
          <h2 className="mt-3 font-display text-3xl sm:text-5xl leading-tight text-[#0B132B]">Choose Your Career-Readiness Program</h2>
          <p className="mt-5 text-slate-600">Three focused pathways — pick the one that matches where you are and how far you want to go.</p>
          <div className="mt-3 text-xs text-slate-500">Instant online enrolment via secure Stripe checkout · You'll receive a confirmation immediately.</div>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-3 items-start">
          {list.map((p, i) => (
            <TiltCard key={p.id || p.name} program={p} i={i} highlighted={!!p.badge}
                      onEnrol={setModal}
                      checkingOut={checkingOut === (p.id || p.name)} />
          ))}
        </div>
      </div>
      {modal && (
        <EnrolModal program={modal} onClose={() => setModal(null)} onSubmit={(buyer) => { setModal(null); startCheckout(modal, buyer); }} />
      )}
    </section>
  );
}

function EnrolModal({ program, onClose, onSubmit }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const submit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 z-[70] bg-[#0B132B]/60 backdrop-blur grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="enrol-form"
            className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-high">
        <div className="text-[11px] tracking-widest uppercase text-slate-500">Enrol</div>
        <div className="font-display text-2xl text-[#0B132B] mt-1">{program.name}</div>
        <div className="mt-1 text-sm text-slate-500">{program.duration} · {program.price}</div>
        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Full Name</span>
            <input required value={form.full_name} onChange={upd("full_name")} data-testid="enrol-name"
                   className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Email</span>
            <input required type="email" value={form.email} onChange={upd("email")} data-testid="enrol-email"
                   className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Phone / WhatsApp</span>
            <input required value={form.phone} onChange={upd("phone")} data-testid="enrol-phone"
                   className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
          </label>
        </div>
        <button data-testid="enrol-submit" className="mt-6 w-full h-11 rounded-full bg-[#0B132B] text-white text-sm font-semibold hover:-translate-y-0.5 transition-transform">
          Continue to Secure Checkout
        </button>
        <div className="mt-3 text-[11px] text-slate-500 text-center">Powered by Stripe · Test mode uses USD equivalent for now — displayed price is {program.price}.</div>
      </form>
    </div>
  );
}
